/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useContext, useId, useMemo, useState } from "react"
import { TbChevronRight } from "react-icons/tb"
import { formatTableCell, humanizeColumnName, numericCell, tableColumnModels } from "./dashboard-widget-data"
import { DashboardKpiContext } from "./KpiWidget"
import { TABLE_TOKENS } from "./tokens/table"
import { EditorNumberInput, EditorSection } from "./WidgetEditorFields"

export function TableSettingsEditor(props) {
  const [selected, setSelected] = useState(null)
  const id = useId()
  const data = props.data
  const formats = {
    auto: props.labels.autoFormat,
    text: props.labels.textFormat,
    number: props.labels.numberFormat,
    integer: props.labels.integerFormat,
    percent: props.labels.percentFormat,
    // A percent that always shows ▲/▼ in green / red (returns, changes).
    percent_change: props.labels.percentChangeFormat || "Percent change (▲/▼)",
  }
  const operations = {
    none: props.labels.none,
    count: props.labels.aggregationCount,
    sum: props.labels.aggregationSum,
    avg: props.labels.aggregationAverage,
    min: props.labels.aggregationMin,
    max: props.labels.aggregationMax,
  }
  const updateColumn = (field, column, value) =>
    props.onChange({ [field]: { ...(data[field] || {}), [column]: value } })
  // The defaults the table renders with when no label / format is set, shown here as hints.
  const models = useMemo(
    () =>
      Object.fromEntries(
        tableColumnModels(props.headers, props.table?.rows || [], data, {
          sampleRows: TABLE_TOKENS.inferenceSampleRows,
          longText: TABLE_TOKENS.longText,
        }).map((model) => [model.key, model]),
      ),
    [props.headers, props.table?.rows, data.columnLabels, data.columnFormats],
  )
  const kpi = useContext(DashboardKpiContext)
  return (
    <div className="datox-react-dashboard__table-settings">
      <EditorSection title={props.labels.columnFormatting} badge={props.headers.length} open>
        <div className="datox-react-dashboard__column-settings-list">
          {props.headers.map((column, index) => {
            const format = data.columnFormats?.[column]?.format || "auto"
            const model = models[column]
            const inferredFormat = {
              percent: "percent",
              integer: "integer",
              money: "number",
              ratio: "number",
              number: "number",
            }[model?.inferred.kind] || "text"
            const sample = props.table?.rows?.find((row) => row[column] != null && String(row[column]).trim())?.[column]
            const expanded = selected === column
            return (
              <div key={column} className="datox-react-dashboard__column-settings-card">
                <button
                  type="button"
                  className="datox-react-dashboard__column-settings-trigger"
                  aria-expanded={expanded}
                  aria-controls={`${id}-${index}`}
                  onClick={() => setSelected(expanded ? null : column)}
                >
                  <span>
                    <strong>{model?.label || column}</strong>
                    <small>{format === "auto" ? `${formats.auto} · ${formats[inferredFormat]}` : formats[format]}</small>
                  </span>
                  <TbChevronRight size={16} aria-hidden="true" />
                </button>
                {expanded ? (
                  <div id={`${id}-${index}`} className="datox-react-dashboard__settings-content">
                    <small className="datox-react-dashboard__muted-text">
                      {props.labels.sourceColumn.replace("{column}", column)}
                    </small>
                    <label>
                      <span>{props.labels.columnDisplayName}</span>
                      <input
                        className="datox-react-dashboard__input"
                        maxLength={300}
                        value={data.columnLabels?.[column] || ""}
                        placeholder={humanizeColumnName(column) || column}
                        onChange={(event) => updateColumn("columnLabels", column, event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      <span>{props.labels.valueFormat}</span>
                      <select
                        className="datox-react-dashboard__select"
                        value={format}
                        onChange={(event) =>
                          updateColumn("columnFormats", column, {
                            ...data.columnFormats?.[column],
                            format: event.currentTarget.value,
                          })
                        }
                      >
                        {Object.entries(formats).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {["number", "percent", "percent_change"].includes(format) ? (
                      <label>
                        <span>{props.labels.decimalPlaces}</span>
                        <EditorNumberInput
                          value={data.columnFormats?.[column]?.decimals ?? 2}
                          min={0}
                          max={6}
                          onChange={(decimals) =>
                            updateColumn("columnFormats", column, { ...data.columnFormats?.[column], decimals })
                          }
                        />
                      </label>
                    ) : null}
                    {sample != null ? (
                      <div className="datox-react-dashboard__format-preview" role="status">
                        <span>{props.labels.formatPreview}</span>
                        <strong>{model ? formatTableCell(sample, model.format, kpi?.locale) : String(sample)}</strong>
                      </div>
                    ) : null}
                    {format === "percent" || format === "percent_change" ? (
                      <small className="datox-react-dashboard__muted-text">{props.labels.percentFormatHelp}</small>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </EditorSection>
      <EditorSection title={props.labels.summaryLabel}>
        <label className="datox-react-dashboard__toggle-row">
          <input
            type="checkbox"
            checked={Boolean(data.showSummary)}
            onChange={(event) =>
              props.onChange({ showSummary: event.currentTarget.checked, summaryScope: "applicable_rows" })
            }
          />
          <span>{props.labels.showSummary}</span>
        </label>
        {data.showSummary ? (
          <>
            <p className="datox-react-dashboard__settings-help">{props.labels.summaryScope}</p>
            {props.headers.map((column) => {
              const numeric = props.table?.rows?.some((row) => numericCell(row[column]) !== null)
              const selected = data.summaryColumns?.[column] || "none"
              return (
                <label key={column} className="datox-react-dashboard__summary-setting">
                  <span title={column}>{data.columnLabels?.[column] || column}</span>
                  <select
                    className="datox-react-dashboard__select"
                    aria-label={props.labels.columnSummary.replace("{column}", column)}
                    value={selected}
                    onChange={(event) => updateColumn("summaryColumns", column, event.currentTarget.value)}
                  >
                    {Object.entries(operations)
                      .filter(([operation]) => numeric || ["none", "count", selected].includes(operation))
                      .map(([operation, label]) => (
                        <option key={operation} value={operation}>
                          {label}
                        </option>
                      ))}
                  </select>
                </label>
              )
            })}
          </>
        ) : null}
      </EditorSection>
      <EditorSection title={props.labels.paginationSettings}>
        <label className="datox-react-dashboard__toggle-row">
          <input
            type="checkbox"
            checked={data.pagination !== false}
            onChange={(event) => props.onChange({ pagination: event.currentTarget.checked })}
          />
          <span>{props.labels.paginateRows}</span>
        </label>
        {data.pagination !== false ? (
          <label>
            <span>{props.labels.pageSize}</span>
            <EditorNumberInput
              value={data.pageSize || 20}
              min={5}
              max={1000}
              onChange={(pageSize) => props.onChange({ pageSize })}
            />
          </label>
        ) : null}
      </EditorSection>
    </div>
  )
}
